<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="external-apps-config">
    <!-- Use Favicon Option -->
    <div class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.externalApps.useFavicon')"
        :help="$t('dashboardAdmin.externalApps.useFaviconHelp')"
      />
      <PrimeCheckbox
        :model-value="config.useFavicon"
        binary
        @update:model-value="updateUseFavicon"
      />
    </div>

    <!-- Links List -->
    <div class="field">
      <div class="links-header">
        <label class="links-label">{{
          $t("dashboardAdmin.externalApps.linksList")
        }}</label>
        <PrimeButton
          :label="$t('dashboardAdmin.externalApps.addLink')"
          icon="ri-add-line"
          @click="openAddDialog"
          size="small"
        />
      </div>

      <!-- Empty State -->
      <div v-if="!links || links.length === 0" class="empty-state">
        <i class="ri-link empty-icon"></i>
        <p class="empty-text">
          {{ $t("dashboardAdmin.externalApps.noLinks") }}
        </p>
      </div>

      <!-- Links Grid -->
      <div v-else class="links-container">
        <div v-for="(link, index) in links" :key="index" class="link-card">
          <!-- Icon Preview -->
          <div class="link-icon-container">
            <template v-if="config.useFavicon && link.link">
              <img
                :src="getFaviconUrl(link.link)"
                alt="favicon"
                width="32"
                height="32"
                style="object-fit: contain"
                @error="onFaviconError"
              />
            </template>
            <template v-else>
              <i
                :class="link.icon || getIconForType(link.type)"
                class="link-icon-large"
                :style="{
                  color: link.color ? `#${link.color}` : 'var(--p-text-color)',
                }"
              ></i>
            </template>
          </div>

          <!-- Link Info -->
          <div class="link-info">
            <div class="link-name">
              {{ link.value || getLabelForType(link.type) }}
            </div>
            <div class="link-url">
              {{ link.link }}
            </div>
          </div>

          <!-- Actions -->
          <div class="link-actions">
            <PrimeButton
              icon="ri-pencil-line"
              text
              rounded
              @click="editLink(index)"
              v-tooltip.top="$t('dashboardAdmin.externalApps.edit')"
            />
            <PrimeButton
              icon="ri-delete-bin-line"
              text
              rounded
              severity="danger"
              @click="deleteLink(index)"
              v-tooltip.top="$t('dashboardAdmin.externalApps.delete')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Link Dialog -->
    <PrimeDialog
      v-model:visible="showDialog"
      :header="
        editingIndex !== null
          ? $t('dashboardAdmin.externalApps.editLinkTitle')
          : $t('dashboardAdmin.externalApps.addLinkTitle')
      "
      modal
      :style="{ width: '90vw', maxWidth: '500px' }"
    >
      <div class="dialog-form">
        <!-- Type Selector -->
        <div class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.type")
          }}</label>
          <PrimeSelect
            v-model="currentLink.type"
            :options="availableTypes"
            option-label="label"
            option-value="value"
            :placeholder="$t('dashboardAdmin.externalApps.selectType')"
            class="select-full"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="type-option">
                <i :class="getIconForType(slotProps.value)"></i>
                <span>{{ getLabelForType(slotProps.value) }}</span>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="type-option">
                <i :class="slotProps.option.icon"></i>
                <span>{{ slotProps.option.label }}</span>
              </div>
            </template>
          </PrimeSelect>
        </div>

        <!-- Link URL -->
        <div class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.url")
          }}</label>
          <PrimeInputText
            v-model="currentLink.link"
            :placeholder="$t('dashboardAdmin.externalApps.urlPlaceholder')"
            class="input-full"
          />
        </div>

        <!-- Custom Name (Value) -->
        <div class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.customName")
          }}</label>
          <PrimeInputText
            v-model="currentLink.value"
            :placeholder="
              $t('dashboardAdmin.externalApps.customNamePlaceholder')
            "
            class="input-full"
          />
          <small class="hint-text">{{
            $t("dashboardAdmin.externalApps.customNameHelp")
          }}</small>
        </div>

        <!-- Icon Customization (only if not using favicon) -->
        <div v-if="!config.useFavicon" class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.icon")
          }}</label>
          <div class="icon-picker-row">
            <PrimeButton
              :label="
                currentLink.icon || $t('dashboardAdmin.externalApps.selectIcon')
              "
              icon="ri-emotion-line"
              @click="showIconPicker = true"
              class="icon-picker-btn"
            />
            <PrimeButton
              v-if="currentLink.icon"
              icon="ri-close-line"
              severity="secondary"
              @click="currentLink.icon = ''"
              v-tooltip.top="$t('dashboardAdmin.externalApps.clearIcon')"
            />
          </div>
        </div>

        <!-- Color Picker (only if not using favicon) -->
        <div v-if="!config.useFavicon" class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.color")
          }}</label>
          <div class="color-picker-row">
            <PrimeColorPicker v-model="currentLink.color" format="hex" />
            <span class="color-value">#{{ currentLink.color }}</span>
          </div>
        </div>

        <!-- Icon Preview -->
        <div v-if="currentLink.type" class="field">
          <label class="form-label">{{
            $t("dashboardAdmin.externalApps.preview")
          }}</label>
          <div class="preview-box">
            <div class="preview-content">
              <template v-if="config.useFavicon && currentLink.link">
                <img
                  :src="getFaviconUrl(currentLink.link)"
                  alt="favicon"
                  width="32"
                  height="32"
                  style="object-fit: contain"
                  @error="onFaviconError"
                />
              </template>
              <template v-else>
                <i
                  :class="currentLink.icon || getIconForType(currentLink.type)"
                  class="preview-icon"
                  :style="{
                    color: currentLink.color
                      ? `#${currentLink.color}`
                      : 'var(--p-text-color)',
                  }"
                ></i>
              </template>
              <span class="preview-label">{{
                currentLink.value || getLabelForType(currentLink.type)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <PrimeButton
          :label="$t('dashboardAdmin.cancel')"
          text
          @click="showDialog = false"
        />
        <PrimeButton
          :label="$t('dashboardAdmin.save')"
          @click="saveLink"
          :disabled="!currentLink.type || !currentLink.link"
        />
      </template>
    </PrimeDialog>

    <!-- Icon Picker Dialog -->
    <IconPickerDialog
      v-model="showIconPicker"
      :current-icon="currentLink.icon"
      @select="handleIconSelect"
    />
  </div>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Select from "primevue/select";
import InputText from "primevue/inputtext";
import ColorPicker from "primevue/colorpicker";
import FieldLabel from "@/components/dashboard/editor/FieldLabel.vue";
import IconPickerDialog from "@/components/IconPickerDialog.vue";

export default {
  name: "ExternalAppsConfig",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeCheckbox: Checkbox,
    PrimeSelect: Select,
    PrimeInputText: InputText,
    PrimeColorPicker: ColorPicker,
    FieldLabel,
    IconPickerDialog,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    linksJson: {
      type: String,
      default: "",
    },
  },
  emits: ["update:config", "update:links-json"],
  data() {
    return {
      links: [],
      showDialog: false,
      showIconPicker: false,
      editingIndex: null,
      currentLink: {
        type: "",
        link: "",
        value: "",
        icon: "",
        color: "",
      },
      // Type definitions matching ExternalApps.vue
      typeMap: {
        calendar: { label: "Calendar", icon: "ri-calendar-line" },
        chat: { label: "Chat", icon: "ri-chat-3-line" },
        contacts: { label: "Contacts", icon: "ri-contacts-line" },
        docs: { label: "Docs", icon: "ri-file-text-line" },
        email: { label: "Email", icon: "ri-mail-line" },
        storage: { label: "File storage", icon: "ri-folder-line" },
        iam: { label: "IAM", icon: "ri-home-5-line" },
        notes: { label: "Notes", icon: "ri-edit-line" },
        projects: { label: "Projects", icon: "ri-focus-2-line" },
        tasks: { label: "Tasks", icon: "ri-list-check-2" },
        video: { label: "Video calls", icon: "ri-video-chat-line" },
        wiki: { label: "Wiki", icon: "ri-lightbulb-line" },
        other: { label: "Other", icon: "ri-question-line" },
      },
    };
  },
  computed: {
    availableTypes() {
      return Object.keys(this.typeMap).map((key) => ({
        value: key,
        label: this.typeMap[key].label,
        icon: this.typeMap[key].icon,
      }));
    },
  },
  watch: {
    linksJson: {
      immediate: true,
      handler(newValue) {
        this.parseLinksJson(newValue);
      },
    },
  },
  methods: {
    parseLinksJson(json) {
      try {
        if (json && json.trim()) {
          this.links = JSON.parse(json);
        } else {
          this.links = [];
        }
      } catch (e) {
        console.error("Failed to parse links JSON:", e);
        this.links = [];
      }
    },
    updateUseFavicon(value) {
      this.$emit("update:config", {
        ...this.config,
        useFavicon: value,
      });
    },
    openAddDialog() {
      this.editingIndex = null;
      this.currentLink = {
        type: "",
        link: "",
        value: "",
        icon: "",
        color: "",
      };
      this.showDialog = true;
    },
    editLink(index) {
      this.editingIndex = index;
      this.currentLink = {
        type: this.links[index].type || "",
        link: this.links[index].link || "",
        value: this.links[index].value || "",
        icon: this.links[index].icon || "",
        color: this.links[index].color || "",
      };
      this.showDialog = true;
    },
    handleIconSelect(icon) {
      this.currentLink.icon = icon;
    },
    deleteLink(index) {
      const linkToDelete = this.links[index];
      this.$confirm.require({
        message: this.$t("dashboardAdmin.externalApps.confirmDeleteLink", {
          linkName:
            linkToDelete.value || this.getLabelForType(linkToDelete.type),
        }),
        header: this.$t("dashboardAdmin.deleteConfirmation"),
        icon: "ri-alert-line",
        acceptLabel: this.$t("dashboardAdmin.delete"),
        rejectLabel: this.$t("dashboardAdmin.cancel"),
        accept: () => {
          const newLinks = [...this.links];
          newLinks.splice(index, 1);
          this.links = newLinks;
          this.emitLinksUpdate();
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("dashboardAdmin.externalApps.linkDeleted"),
            life: 2000,
          });
        },
      });
    },
    saveLink() {
      if (!this.currentLink.type || !this.currentLink.link) {
        return;
      }

      const newLinks = [...this.links];
      if (this.editingIndex !== null) {
        // Edit existing link
        newLinks[this.editingIndex] = { ...this.currentLink };
      } else {
        // Add new link
        newLinks.push({ ...this.currentLink });
      }

      this.links = newLinks;
      this.emitLinksUpdate();
      this.showDialog = false;
    },
    emitLinksUpdate() {
      const json = JSON.stringify(this.links);
      this.$emit("update:links-json", json);
    },
    getIconForType(type) {
      return this.typeMap[type]?.icon || "ri-question-line";
    },
    getLabelForType(type) {
      return this.typeMap[type]?.label || type;
    },
    getFaviconUrl(link) {
      try {
        const url = new URL(
          link.startsWith("http") ? link : window.location.origin + link,
        );
        return `${url.origin}/favicon.ico`;
      } catch (e) {
        return "";
      }
    },
    onFaviconError(event) {
      // Fallback to icon if favicon fails to load
      event.target.style.display = "none";
    },
  },
};
</script>

<style scoped>
.external-apps-config {
  max-width: 100%;
  overflow: hidden;
}

.field {
  margin-bottom: 1.5rem;
  max-width: 100%;
  overflow: hidden;
}

.links-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.links-label {
  font-weight: 600;
}

.empty-state {
  text-align: center;
  padding: 1rem;
  background: var(--p-surface-100);
  border-radius: var(--p-border-radius);
}

.empty-icon {
  font-size: 2rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.5rem;
}

.empty-text {
  color: var(--p-text-muted-color);
  margin: 0;
}

/* Links container should not overflow */
.links-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 100%;
  overflow: hidden;
}

/* Link card should not overflow */
.link-card {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-radius: var(--p-border-radius);
  background: var(--p-surface-100);
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.link-icon-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-border-radius);
  width: 48px;
  height: 48px;
}

.link-icon-large {
  font-size: 1.5rem;
}

.link-info {
  flex-grow: 1;
  overflow: hidden;
}

.link-name {
  font-weight: 600;
}

.link-url {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.link-actions {
  display: flex;
}

.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
}

.select-full {
  width: 100%;
}

.input-full {
  width: 100%;
}

.hint-text {
  color: var(--p-text-muted-color);
}

.type-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon-picker-row {
  display: flex;
  gap: 0.5rem;
}

.icon-picker-btn {
  flex-grow: 1;
}

.color-picker-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.color-value {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.preview-box {
  background: var(--p-surface-100);
  border-radius: var(--p-border-radius);
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
}

.preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.preview-icon {
  font-size: 2rem;
}

.preview-label {
  font-size: 0.875rem;
}

/* Ensure inputs and dropdowns fit properly */
:deep(.p-inputtext),
:deep(.p-select) {
  max-width: 100%;
  box-sizing: border-box;
}
</style>
